/**
 * useQuotaModals - Modal state management for quota/IP limit modals
 *
 * Extracted from useUniversalQuota.ts (2025-12-15) for:
 * - Separation of concerns
 * - Reusability across screens
 * - Cleaner state management
 *
 * @module hooks/quota/useQuotaModals
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import { captureEvent } from '../../lib/analytics';
import type { UniversalQuotaData } from './quotaTypes';

export interface UseQuotaModalsResult {
  /** Whether quota exceeded modal is visible */
  showQuotaModal: boolean;
  /** Set quota modal visibility */
  setShowQuotaModal: (show: boolean) => void;
  /** Whether IP rate limit modal is visible */
  showIpLimitModal: boolean;
  /** Set IP limit modal visibility */
  setShowIpLimitModal: (show: boolean) => void;
  /** Reset modal guards (call when error state clears) */
  resetModalGuards: () => void;
}

interface UseQuotaModalsOptions {
  /** API error object (for detecting quota/IP errors) */
  // DD-21: Use `unknown` for message type to handle runtime cases where
  // the API returns a non-string message (object, array, etc.)
  apiError: { message?: unknown; code?: string } | null;
  /** Current quota data (for analytics) */
  quotaData: UniversalQuotaData | null;
  /** Whether the current user is anonymous (trial user without account) */
  isAnonymous?: boolean;
}

/**
 * Detect if error is a quota exceeded error
 */
export function isQuotaExceededError(
  apiError: { message?: unknown; code?: string } | null
): boolean {
  if (!apiError) return false;
  // Type guard: ensure message is a string before calling .includes()
  const message = typeof apiError.message === 'string' ? apiError.message : '';
  return (
    message.includes('Trial limit reached') ||
    message.includes('QUOTA_EXCEEDED') ||
    message.includes('Daily quota exceeded') ||
    message.includes('Weekly quota exceeded') ||
    apiError.code === 'QUOTA_EXCEEDED'
  );
}

/**
 * Detect if error is an IP rate limit error
 */
export function isIpRateLimitedError(
  apiError: { message?: unknown; code?: string } | null
): boolean {
  if (!apiError) return false;
  // Type guard: ensure message is a string before calling .includes()
  const message = typeof apiError.message === 'string' ? apiError.message : '';
  return (
    message.includes('IP rate limit exceeded') ||
    message.includes('IP_QUOTA_EXCEEDED') ||
    apiError.code === 'IP_QUOTA_EXCEEDED'
  );
}

/**
 * Hook for managing quota and IP limit modal state
 *
 * Features:
 * - Auto-shows modals when errors detected
 * - Prevents infinite modal loop after dismissal
 * - Tracks analytics events
 *
 * @param options - Configuration options
 * @returns Modal state and handlers
 *
 * @example
 * ```typescript
 * const {
 *   showQuotaModal,
 *   setShowQuotaModal,
 *   showIpLimitModal,
 *   setShowIpLimitModal,
 * } = useQuotaModals({ apiError, quotaData });
 *
 * return (
 *   <>
 *     <QuotaExceededModal visible={showQuotaModal} onClose={() => setShowQuotaModal(false)} />
 *     <IpLimitModal visible={showIpLimitModal} onClose={() => setShowIpLimitModal(false)} />
 *   </>
 * );
 * ```
 */
export function useQuotaModals({
  apiError,
  quotaData,
  isAnonymous = false,
}: UseQuotaModalsOptions): UseQuotaModalsResult {
  // Modal state
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showIpLimitModal, setShowIpLimitModal] = useState(false);

  // Guards to prevent infinite modal loop after dismissal
  const hasShownQuotaModalRef = useRef(false);
  const hasShownIpLimitModalRef = useRef(false);

  // Derive error states
  const isQuotaExceeded = isQuotaExceededError(apiError);
  const isIpRateLimited = isIpRateLimitedError(apiError);

  // Reset modal guards when error state changes
  useEffect(() => {
    if (!isQuotaExceeded) {
      hasShownQuotaModalRef.current = false;
    }
    if (!isIpRateLimited) {
      hasShownIpLimitModalRef.current = false;
    }
  }, [isQuotaExceeded, isIpRateLimited]);

  // Show quota modal when quota exceeded
  useEffect(() => {
    if (isQuotaExceeded && !hasShownQuotaModalRef.current) {
      hasShownQuotaModalRef.current = true;
      setShowQuotaModal(true);

      // NocoDB #792: Fire Trial_LimitReached - primary quota-wall conversion event
      captureEvent('Trial_LimitReached', {
        user_tier: quotaData?.tier || (isAnonymous ? 'anonymous' : 'free'),
        quota_limit: quotaData?.limit || 0,
        analysis_count: quotaData?.used || 0,
      });
      // Keep legacy event for backward-compat dashboards
      captureEvent('quota_limit_reached', {
        platform: Platform.OS,
        quota_source: quotaData?.source || 'unknown',
        quota_limit: quotaData?.limit || 0,
        reset_period: quotaData?.resetPeriod || 'unknown',
      });
    }
  }, [isQuotaExceeded, quotaData, isAnonymous]);

  // Show IP limit modal when IP rate limited
  useEffect(() => {
    if (isIpRateLimited && !hasShownIpLimitModalRef.current) {
      hasShownIpLimitModalRef.current = true;
      setShowIpLimitModal(true);

      captureEvent('ip_rate_limit_reached', {
        platform: Platform.OS,
      });
    }
  }, [isIpRateLimited]);

  // Manual reset function
  const resetModalGuards = useCallback(() => {
    hasShownQuotaModalRef.current = false;
    hasShownIpLimitModalRef.current = false;
  }, []);

  return {
    showQuotaModal,
    setShowQuotaModal,
    showIpLimitModal,
    setShowIpLimitModal,
    resetModalGuards,
  };
}
